import { Graph } from 'graphlib';
import { Dispatch } from 'redux';
import AutomationController, {
  DeleteAutomationParams,
} from '../controllers/AutomationController';
import { CampaignsOverviewController } from '../controllers/CampaignsOverviewController';
import {
  IAutomationAction,
  IAutomationActionToOpenOnReady,
} from '../model/AutomationModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';
import {
  CampaignAudienceAttribute,
  CampaignType,
} from '../model/CampaignsModel';
import CampaignsActions from './CampaignsCRUDActions';
import { isPromoter, PromoterModel } from '../model/PromoterModel';
import TriggerModel from '../model/TriggerModel';
import modalActions from '../modules/modalsState/actions';
import { getErrorJson } from '@mm/utils';
import { selectCampaignById } from '../selectors/campaignSelectors';
import PromoterCRUDActions from './PromoterCRUDActions';

export const AutomationActions = {
  SHOW_ACTIONS_LIST: 'SHOW_ACTIONS_LIST',
  HIDE_ACTIONS_LIST: 'HIDE_ACTIONS_LIST',
  SET_GRAPHLIB_INSTANCE: 'SET_GRAPHLIB_INSTANCE',
  RESET_AUTOMATIONS_STATE: 'RESET_AUTOMATIONS_STATE',
  RETRIEVE_STATISTICS: 'RETRIEVE_STATISTICS',
  RETRIEVE_STATISTICS_SUCCESS: 'RETRIEVE_STATISTICS_SUCCESS',
  RETRIEVED_AGGREGATE_STATISTICS: 'RETRIEVED_AGGREGATE_STATISTICS',
  PUSH_ACTION_TO_OPEN: 'PUSH_ACTION_TO_OPEN',
  REMOVE_ACTION_TO_OPEN: 'REMOVE_ACTION_TO_OPEN',
  INCREASE_REVIEW_REQUIRED: 'INCREASE_REVIEW_REQUIRED',
  DECREASE_REVIEW_REQUIRED: 'DECREASE_REVIEW_REQUIRED',
  SET_REVIEW_REQUIRED_COUNT: 'SET_REVIEW_REQUIRED_COUNT',
  RESET_REVIEW_REQUIRED_COUNT: 'RESET_REVIEW_REQUIRED_COUNT',
  RETRIEVED_EXISTING_TRIGGERS: 'RETRIEVED_EXISTING_TRIGGERS',
  RETRIEVED_VERIFIED_STATS: 'RETRIEVED_VERIFIED_STATS',
};

export const showActionsList = (payload?: IAutomationAction) => ({
  type: AutomationActions.SHOW_ACTIONS_LIST,
  payload,
});

export const hideActionsList = () => ({
  type: AutomationActions.HIDE_ACTIONS_LIST,
});

export const resetActionsState = () => ({
  type: AutomationActions.RESET_AUTOMATIONS_STATE,
});

export const pushActionToOpen = (payload: IAutomationActionToOpenOnReady) => ({
  type: AutomationActions.PUSH_ACTION_TO_OPEN,
  payload,
});

export const increaseReviewRequiredCount = () => ({
  type: AutomationActions.INCREASE_REVIEW_REQUIRED,
});

export const decreaseReviewRequiredCount = () => ({
  type: AutomationActions.DECREASE_REVIEW_REQUIRED,
});

export const setReviewRequiredCount = (payload: number | null) => ({
  type: AutomationActions.SET_REVIEW_REQUIRED_COUNT,
  payload,
});

export const removeActionToOpen = (
  payload: IAutomationActionToOpenOnReady,
) => ({
  type: AutomationActions.REMOVE_ACTION_TO_OPEN,
  payload,
});

export const setGraphLibInstance = (payload: Graph | undefined) => ({
  type: AutomationActions.SET_GRAPHLIB_INSTANCE,
  payload,
});

export const diffAudienceAttributes = (l?: CampaignType, r?: CampaignType) => {
  return (
    l?.campaignAudiencesAttributes?.filter(
      lv =>
        !r?.campaignAudiencesAttributes?.some(
          rv => rv.audienceId === lv.audienceId,
        ),
    ) || []
  );
};

export const getPromoterOrTrigger = (
  attr?: CampaignAudienceAttribute,
): PromoterModel | TriggerModel | undefined => {
  switch (attr?.triggerType) {
    case 'promoter':
      return attr.promoter;
    case 'trigger':
    case 'page':
      return attr.trigger;
    default:
      return undefined;
  }
};

const actions = {
  showActionsList,
  hideActionsList,
  resetActionsState,
  setGraphLibInstance,
  pushActionToOpen,
  removeActionToOpen,
  increaseReviewRequiredCount,
  decreaseReviewRequiredCount,
  setReviewRequiredCount,
};

export const useAutomationActions = getActionHookBuilder(actions, [
  'showActionsList',
  'hideActionsList',
  'resetActionsState',
  'setGraphLibInstance',
  'pushActionToOpen',
  'removeActionToOpen',
  'increaseReviewRequiredCount',
  'decreaseReviewRequiredCount',
  'setReviewRequiredCount',
]);

class AutomationsCRUDActions extends CrudActionContainer {
  constructor() {
    super('AUTOMATIONS');
  }

  retrieveStatistics(
    ...args: Parameters<typeof AutomationController.getStats>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      const [botId, campaignId] = args;
      dispatch(this.busy());

      dispatch({
        type: AutomationActions.RETRIEVE_STATISTICS,
        payload: { campaignId },
      });

      try {
        const payload = await AutomationController.getStats(...args);

        dispatch({
          type: AutomationActions.RETRIEVE_STATISTICS_SUCCESS,
          payload: {
            campaignId,
            stats: payload,
          },
        });

        return payload;
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveAggregateStatistics(
    ...args: Omit<
      Parameters<typeof AutomationController.getStats>,
      'campaingId'
    >
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      const [botId] = args;
      dispatch(this.busy());

      try {
        const [aggregateStats, automationsList] = await Promise.all([
          AutomationController.getStats(botId),
          CampaignsOverviewController.getAll({
            botId,
            kind: 'automation',
            pagination: {
              page: 1,
              perPage: 1,
            },
          }),
        ]);

        const payload = {
          ...aggregateStats,
          automationsCount: automationsList?.pagination?.total || 0,
        };

        dispatch({
          type: AutomationActions.RETRIEVED_AGGREGATE_STATISTICS,
          payload,
        });

        return payload;
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  createInputTrigger(
    ...args: Parameters<typeof AutomationController.createInputTrigger>
  ) {
    return async (dispatch: Dispatch<StoreState>, getStore) => {
      dispatch(this.busy());

      try {
        const entity: CampaignType =
          await AutomationController.createInputTrigger(...args);

        if (entity.id) {
          dispatch({
            type: CampaignsActions.updateSuccessAction,
            entity,
          });
        }

        return entity;
      } catch (e) {
        if (e instanceof Response) {
          const json = await getErrorJson(e);
          if (json.error) {
            dispatch(
              modalActions.openPrettyErrorModal({
                title: 'Ooops!',
                errorMessage: json.error,
              }),
            );
          }
        }

        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getExistingInputTriggers(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());

      try {
        const payload = await AutomationController.getExistingInputTriggers(
          botId,
        );

        dispatch({
          type: AutomationActions.RETRIEVED_EXISTING_TRIGGERS,
          payload,
        });

        return payload;
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  sendTestEmail(
    ...args: Parameters<typeof AutomationController.sendTestEmail>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());

      try {
        return await AutomationController.sendTestEmail(...args);
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getEmailCompletion(
    ...args: Parameters<typeof AutomationController.getEmailCompletion>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());

      try {
        return await AutomationController.getEmailCompletion(...args);
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getVerifiedStats(
    ...args: Parameters<typeof AutomationController.getVerifiedStats>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());

      try {
        const payload = await AutomationController.getVerifiedStats(...args);

        dispatch({
          type: AutomationActions.RETRIEVED_VERIFIED_STATS,
          payload,
        });

        return payload;
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  deleteAutomation(params: DeleteAutomationParams) {
    return async (dispatch: Dispatch<StoreState>, getState) => {
      const state: StoreState = getState();
      const automationCampaign = selectCampaignById(state, params.id);

      dispatch(this.busy());

      try {
        const res = await AutomationController.deleteAutomation(params);
        const body = await res.json();

        if (body.success) {
          // delete local related promoters
          if (params.destroyTriggers || params.destroyAll) {
            const promoters =
              automationCampaign?.campaignAudiencesAttributes
                ?.map(getPromoterOrTrigger)
                .filter(Boolean)
                .filter(isPromoter) || [];

            promoters.forEach(promoter => {
              dispatch(PromoterCRUDActions.delete(promoter));
            });
          }

          // delete campaign
          dispatch({
            type: CampaignsActions.deleteAction,
            payload: params.id,
          });
        }

        return params.id;
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }
}

const automationCRUDActions = new AutomationsCRUDActions();

export const useAutomationsCRUDActions = getActionHookBuilder(
  automationCRUDActions,
  [
    'createInputTrigger',
    'retrieveStatistics',
    'retrieveAggregateStatistics',
    'sendTestEmail',
    'deleteAutomation',
    'getExistingInputTriggers',
    'getEmailCompletion',
    'getVerifiedStats',
  ],
);
